import { cn } from '@my/ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { fmtDayLabel, formatCutoff, money, STATUS_BADGE, STATUS_DOT } from '../lib/format';
import type { BookingRow, RouteOption } from '../lib/mockData';
import { BookingDetails } from './BookingDetails';
import { BookingExpand } from './BookingExpand';
import { SailingTypePill } from './SailingTypePill';

/**
 * One booking queue — a slate-headed table (matches the Orders list) rendering
 * either the To Book or Booked set. Columns mirror the Ferry Booking mock:
 * Order/Customer/Destination · Corridor · Window · Best route · Status · Cutoff.
 * Booked rows differ only in that their best route carries a sail time. Rows
 * with ranked alternatives expose a "+N alt" toggle that expands them inline.
 */
export function BookingQueue({
  title,
  count,
  rows,
  emptyHint,
  expandable = false,
  orgId = null,
}: {
  title: string;
  count: number;
  rows: BookingRow[];
  emptyHint: string;
  /** To Book queue: double-click a row to expand the booking screen inline. */
  expandable?: boolean;
  /** Owning org — passed to the expand panel so the ✓ can create a booking. */
  orgId?: string | null;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <section>
      <div className="flex items-center gap-2 px-1 pb-1">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
        <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-slate-200 px-1 text-[10px] font-semibold text-slate-600">
          {count}
        </span>
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full table-auto text-xs">
          <thead className="bg-slate-700 text-[10px] uppercase tracking-wide text-white">
            <tr>
              <Th>Job # · Customer · Destination</Th>
              {/* Reference only applies once booked — Booked queue only. */}
              {!expandable && <Th>Reference</Th>}
              <Th>Corridor</Th>
              <Th>Delivery</Th>
              <Th>Best route · Arrival port · Road km</Th>
              <Th>Status</Th>
              <Th align="right">Cutoff</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={expandable ? 6 : 7}
                  className="px-3 py-6 text-center text-xs italic text-slate-400"
                >
                  {emptyHint}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <QueueRow
                  key={row.id}
                  row={row}
                  expandable={expandable}
                  orgId={orgId}
                  isOpen={openId === row.id}
                  onToggleOpen={() => setOpenId((id) => (id === row.id ? null : row.id))}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function QueueRow({
  row,
  expandable,
  orgId,
  isOpen,
  onToggleOpen,
}: {
  row: BookingRow;
  expandable: boolean;
  orgId: string | null;
  isOpen: boolean;
  onToggleOpen: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const cutoff = formatCutoff(row.cutoffMinutes);

  return (
    <>
      <tr
        onDoubleClick={onToggleOpen}
        title={
          expandable
            ? 'Double-click to open the booking screen'
            : 'Double-click to view the booking'
        }
        className={cn(
          'align-top cursor-pointer select-none hover:bg-slate-50',
          isOpen && 'bg-blue-50/40',
        )}
      >
        {/* Job # + customer (line 1) · cargo + destination (line 2) */}
        <td className="px-3 py-2.5">
          <div className="flex items-baseline gap-2">
            <span className="whitespace-nowrap text-xs font-semibold text-slate-900">
              {row.orderNumber}
            </span>
            <span className="text-xs font-medium text-slate-800">{row.customer}</span>
          </div>
          <div className="text-[11px] text-slate-400">
            {row.cargoLabel} · {row.destination}
          </div>
        </td>

        {/* Ferry booking reference (Booked queue only) — set once Confirmed. */}
        {!expandable && (
          <td className="whitespace-nowrap px-3 py-2.5">
            {row.bookingRef ? (
              <span className="font-mono text-[11px] text-slate-600">{row.bookingRef}</span>
            ) : (
              <span className="text-[11px] text-slate-300">—</span>
            )}
          </td>
        )}

        {/* Corridor + customs flag */}
        <td className="whitespace-nowrap px-3 py-2.5">
          <span className="inline-flex items-center gap-1">
            <span className="font-medium text-slate-600">{row.corridor}</span>
            {row.customs && (
              <span
                title="Customs corridor"
                className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-100 text-[8px] font-bold text-red-600"
              >
                C
              </span>
            )}
          </span>
        </td>

        {/* Delivery date + window */}
        <td className="whitespace-nowrap px-3 py-2.5">
          <div className="text-xs font-medium text-slate-600">
            {fmtDayLabel(row.deliveryDayOffset)}
          </div>
          <div className="text-[11px] text-slate-400">{row.window}</div>
        </td>

        {/* Best route — alternatives only matter pre-booking (To Book queue). */}
        <td className="px-3 py-2.5">
          {row.best ? (
            <RouteCell
              route={row.best}
              altCount={expandable ? row.alts.length : 0}
              onToggleAlts={() => setExpanded((v) => !v)}
              expanded={expanded}
            />
          ) : (
            <span className="text-[11px] font-medium text-red-600">
              {row.noFitReason ?? 'No sailing fits window'}
            </span>
          )}
        </td>

        {/* Status */}
        <td className="whitespace-nowrap px-3 py-2.5">
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset',
              STATUS_BADGE[row.status],
            )}
          >
            <span className={cn('h-1.5 w-1.5 rounded-full', STATUS_DOT[row.status])} aria-hidden="true" />
            {row.status}
          </span>
        </td>

        {/* Cutoff countdown */}
        <td className={cn('whitespace-nowrap px-3 py-2.5 text-right tabular-nums', cutoff.tone)}>
          {cutoff.label}
        </td>
      </tr>

      {/* Expanded alternatives (To Book only) */}
      {expandable && expanded && row.alts.length > 0 && (
        <tr className="bg-slate-50/60">
          <td />
          <td colSpan={5} className="px-3 pb-2.5">
            <div className="space-y-1 border-l-2 border-slate-200 pl-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Alternative routes
              </div>
              {row.alts.map((alt) => (
                <div key={`${alt.routeShort}-${alt.operator}`} className="flex items-center gap-2">
                  <RouteSummary route={alt} muted />
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}

      {/* Expanded panel (double-click) — sailing picker for To Book, booking form for Booked. */}
      {isOpen && (
        <tr>
          <td colSpan={expandable ? 6 : 7} className="bg-slate-50 p-2">
            {expandable ? (
              <BookingExpand row={row} orgId={orgId} onClose={onToggleOpen} />
            ) : (
              <BookingDetails row={row} onClose={onToggleOpen} />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** Best-route cell — route headline over the arrival-port line (alt toggle inline). */
function RouteCell({
  route,
  altCount,
  expanded,
  onToggleAlts,
}: {
  route: RouteOption;
  altCount: number;
  expanded: boolean;
  onToggleAlts: () => void;
}) {
  return (
    <RouteSummary route={route} altCount={altCount} expanded={expanded} onToggleAlts={onToggleAlts} />
  );
}

/**
 * One route line: "Ross→Pemb 20:30 [type]" headline over
 * "→ Pembroke · 38km · €180 · Irish Ferries" detail, with the "+N alt" toggle
 * sitting inline at the end of the detail line. `muted` dims it for the
 * expanded alternatives (which carry no further toggle).
 */
function RouteSummary({
  route,
  muted,
  altCount = 0,
  expanded = false,
  onToggleAlts,
}: {
  route: RouteOption;
  muted?: boolean;
  altCount?: number;
  expanded?: boolean;
  onToggleAlts?: () => void;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5">
        <span className={cn('font-semibold', muted ? 'text-slate-600' : 'text-slate-800')}>
          {route.routeShort}
        </span>
        {/* Sailing type (off-peak/peak) is only known once a specific sailing is
            booked — show it only when this route carries a sail time. */}
        {route.sailTime && (
          <>
            <span className="tabular-nums text-slate-500">{route.sailTime}</span>
            <SailingTypePill type={route.type} />
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-400">
        {/* Road km / cost come from the (parked) port-optimisation engine — omit
            them until they're known so booked rows don't show "0km · €0". */}
        <span>
          →{route.arrivalPort ? ` ${route.arrivalPort}` : ''}
          {route.roadKm > 0 ? ` · ${route.roadKm}km` : ''}
          {route.cost > 0 ? ` · ${money(route.cost)}` : ''}
          {route.operator ? <span className="text-slate-300"> · {route.operator}</span> : null}
        </span>
        {altCount > 0 && onToggleAlts && (
          <button
            type="button"
            onClick={onToggleAlts}
            className="inline-flex items-center gap-0.5 font-medium text-slate-400 hover:text-slate-600"
          >
            {expanded ? (
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
            )}
            {altCount} alt{altCount > 1 ? 's' : ''}
          </button>
        )}
      </div>
    </div>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th className={cn('whitespace-nowrap px-3 py-2 font-semibold', align === 'right' ? 'text-right' : 'text-left')}>
      {children}
    </th>
  );
}
